/// <reference types="jquery" />
import * as polyfills from "./polyfills";
import { MathJaxObject } from "mathjax-full/js/components/startup";
import { mathjax } from "mathjax-full/js/mathjax";
import Controller, {
	CardController, ContextController, FilterController, FlagController,
	FlashNavigationController, InitCardController, InitListController,
	InitOverviewController, InitReviewController, InitTestController,
	PqNavigationController, SettingsController, TestController, TimerController
} from "./controller";
import { InitializeEvent } from "./events";
import Content from "./lib/content";
import Current from "./lib/current";
import Filter from "./lib/filter";
import Navigation from "./lib/navigation";
import { SwipeConfig } from "./lib/swipe";
import {
	Config as TimerConfig, FlashTimer, FlashTimerExtras, PqTimer, PqTimerExtras,
	Timer
} from "./lib/timer";
import UserData from "./lib/userdata";
import { CardItemModel, CardMetadataModel } from "./model/impl/card";
import {
	CommonListModel, CommonSettingsModel, CommonStateModel
} from "./model/impl/common";
import {
	AttemptId, DefaultSerializerFactory, FlashDataSourceFactory, ItemId,
	PqDataSourceFactory, ProductId, QuizId, SerializerFactory
} from "./model/datasource";
import Model from "./model/model";
import { OverviewMetadataModel, OverviewScoreModel } from "./model/impl/overview";
import {
	ReviewItemModel, ReviewMetadataModel, ReviewScoreModel
} from "./model/impl/review";
import { TestItemModel, TestMetadataModel } from "./model/impl/test";
import { CardChartView, CardHeaderView, CardItemView } from "./view/impl/card";
import { CommonListView, CommonSettingsView } from "./view/impl/common";
import { FlashFrontendFactory, PqFrontendFactory } from "./view/frontend";
import { ListHeaderView } from "./view/impl/list";
import {
	OverviewChartView,
	OverviewSectionView,
	OverviewSummaryView
} from "./view/impl/overview";
import {
	ReviewChartView,
	ReviewHeaderView, ReviewItemView, ReviewScoreView
} from "./view/impl/review";
import { TestChartView, TestHeaderView, TestItemView } from "./view/impl/test";
import View from "./view/view";
import { FlashView, PqView } from "./view/view/view";
import {
	FlashAttemptStateInitializer, GlobalStateInitializer, PqAttemptStateInitializer
} from "./model/datasource/stateinitializer";
import { Polyfill } from "./polyfills";
import { AttemptState, GlobalState } from "./model/datasource/state";

declare global {
	interface Window {
		MathJax: MathJaxObject
	}
}

declare var $j: JQueryStatic;

export interface Config {
	jQuery?: JQueryStatic;
	mathjax?: MathJaxObject;
	swipe?: boolean | SwipeConfig;
	randomizeAnswers?: boolean;
	timer?: TimerConfig;
	mathjaxUrl?: string;
	polyfills?: Polyfill[];
}

interface AppProps<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> {
	model: Model<P, A, Z, I, unknown>;
	view: View<P, A, Z, I>;
	controllers: Controller<P, A, Z, I>[];
	content: Content<P, A, Z, I>;
	current: Current<I>;
	filter: Filter;
	timer: Timer<unknown>;
	userdata: UserData<P, A, Z, I>;
	navigation: Navigation;
	config: Config;
}

export default class App<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> {
	model: Model<P, A, Z, I, unknown>;
	view: View<P, A, Z, I>;
	controllers: Controller<P, A, Z, I>[];
	content: Content<P, A, Z, I>;
	current: Current<I>;
	filter: Filter;
	timer: Timer<unknown>;
	userdata: UserData<P, A, Z, I>;
	navigation: Navigation;
	config: Config;

	constructor({model, view, controllers, content, current, filter, timer, userdata, navigation, config}: AppProps<P, A, Z, I>) {
		/** @type {Model} */
		this.model = model;
		/** @type {View} */
		this.view = view;
		/** @type {Controller[]} */
		this.controllers = controllers;
		/** @type {Content} */
		this.content = content;
		/** @type {Current} */
		this.current = current;
		/** @type {Filter} */
		this.filter = filter;
		/** @type {Timer} */
		this.timer = timer;
		/** @type {UserData} */
		this.userdata = userdata;
		/** @type {Navigation} */
		this.navigation = navigation;

		const bindTarget = view.getBindTarget();
		for (const key in this.controllers) {
			const controller = this.controllers[key];
			controller.bind(bindTarget, this);
		}

		const defaults: Config = {
			jQuery: typeof $j != 'undefined' ? $j : undefined,
			mathjax: typeof window.MathJax != 'undefined' && window.MathJax.typesetPromise ? window.MathJax : undefined,
			randomizeAnswers: false,
			timer: {
				resolution: 100,
				tickRate: 1000,
				pingRate: 30000,
				advanceRate: 30000,
				recentCount: 3,
				timed: false,
				autoAdvance: false,
			},
			swipe: true,
			mathjaxUrl: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/{version}/es5/tex-mml-chtml.min.js',
			polyfills: Object.values(polyfills.default_polyfills),
		};
		this.config = {
			...defaults,
			...config
		};
	}

	init(): void {
		polyfills.init(this.config.polyfills);
		const bindTarget = this.view.getBindTarget();
		Promise.allSettled([
			...(this.config.mathjax ? [] : [this.loadMathJax()])
		]).then(() => {
			bindTarget.dispatchEvent(new InitializeEvent());
		});
	}

	getJquery(): JQueryStatic { return this.config.jQuery; }

	loadMathJax(): Promise<MathJaxObject> {
		return this.view.loadScript({
			src: this.config.mathjaxUrl
				.replace('{version}', mathjax.version)
		}).catch((error) => {
			console.log('Failed to load MathJax', {error});
			window.MathJax = { typesetPromise: () => Promise.resolve() } as any as MathJaxObject;
		}).then(() => {
			this.config.mathjax = window.MathJax;
			return window.MathJax;
		});
	}

	getMathJax(): MathJaxObject { return this.config.mathjax; }

	serialize(indices: string[]): { attempt: AttemptState, global: GlobalState } {
		return {
			attempt: {
				version: 2, timestamp: new Date().toISOString(),
				current: this.current.serialize(indices),
				times: this.timer.serializeTimes(indices)
			},
			global: {
				version: 2, timestamp: new Date().toISOString(),
				timer: this.timer.serializeSettings(),
				settings: {
					fontsize: this.model.settings.getFontSize(),
					randomize: this.model.settings.getRandomize()
				}
			}
		};
	}

	static mergeControllers<
		C extends Record<PropertyKey, Controller<any, any, any, any>>,
	>(
		controllers: Partial<C> | undefined,
		default_controllers: Required<C>,
		extra_controllers?: ArrayLike<Controller<any, any, any, any>>
	): C[keyof C][] & Controller<any, any, any, any>[] {
		return [].concat(
			Object.values({...default_controllers, ...controllers}),
			extra_controllers ?? []
		);
	}
}

export class PqApp<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends App<P, A, Z, I> {
	model: Model<P, A, Z, I, PqTimerExtras>;
	view: PqView<P, A, Z, I>;
	timer: PqTimer;

	static constructOverview<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId>(
		datasource: PqDataSourceFactory<P, A, Z, I>,
		frontend: PqFrontendFactory<P, A, Z, I>,
		config: Config,
		serializer?: SerializerFactory<P, A, Z, I>,
		controllers?: Partial<{
			init: InitOverviewController<P, A, Z, I>,
		}>,
		extra_controllers?: Controller<P, A, Z, I>[],
		libs?: Partial<{
			userdata: UserData<P, A, Z, I>,
		}>,
	): PqApp<P, A, Z, I> {
		serializer ??= new DefaultSerializerFactory();
		const productSerializer = serializer.productSerializer();
		const quizSerializer = serializer.quizSerializer();
		const itemSerializer = serializer.itemSerializer();
		return new this({
			model: new Model({
				metadata: new OverviewMetadataModel(datasource.metadataDataSource()),
				score: new OverviewScoreModel(datasource.scoreDataSource(quizSerializer)),
			}),
			view: new View({
				sections: new OverviewSectionView(frontend.sectionFrontend(quizSerializer, itemSerializer)),
				summary: new OverviewSummaryView(frontend.summaryFrontend(productSerializer)),
				chart: new OverviewChartView(frontend.chartFrontend(productSerializer))
			}),
			controllers: this.mergeControllers(controllers, {
				init: new InitOverviewController()
			}, extra_controllers),
			content: new Content(),
			current: new Current(),
			filter: new Filter(),
			timer: new PqTimer(),
			userdata: libs?.userdata ?? new UserData(),
			navigation: new Navigation(),
			config
		});
	}

	static constructTest<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId>(
		datasource: PqDataSourceFactory<P, A, Z, I>,
		frontend: PqFrontendFactory<P, A, Z, I>,
		config: Config,
		serializer?: SerializerFactory<P, A, Z, I>,
		controllers?: Partial<{
			init: InitTestController<P, A, Z, I>,
			test: TestController<P, A, Z, I>,
			navigation: PqNavigationController<P, A, Z, I>,
			timer: TimerController<P, A, Z, I, PqTimerExtras>,
			context: ContextController<P, A, Z, I>,
			filter: FilterController<P, A, Z, I>,
			settings: SettingsController<P, A, Z, I>,
			flag: FlagController<P, A, Z, I>,
		}>,
		extra_controllers?: Controller<P, A, Z, I>[],
		libs?: Partial<{
			userdata: UserData<P, A, Z, I>,
		}>,
	): PqApp<P, A, Z, I> {
		serializer ??= new DefaultSerializerFactory();
		const itemSerializer = serializer.itemSerializer();
		const productSerializer = serializer.productSerializer();
		return new this({
			model: new Model({
				metadata: new TestMetadataModel(datasource.metadataDataSource()),
				list: new CommonListModel(datasource.listDataSource(itemSerializer)),
				item: new TestItemModel(datasource.itemDataSource()),
				settings: new CommonSettingsModel(datasource.settingsDataSource()),
				state: {
					global: new CommonStateModel(datasource.globalStateDataSource(datasource.globalStateInitializer() ?? new GlobalStateInitializer())),
					attempt: new CommonStateModel(datasource.attemptStateDataSource(datasource.attemptStateInitializer() ?? new PqAttemptStateInitializer()))
				}
			}),
			view: new View({
				item: new TestItemView(frontend.itemFrontend(itemSerializer)),
				list: new CommonListView(frontend.listFrontend(itemSerializer)),
				header: new TestHeaderView(frontend.headerFrontend(itemSerializer)),
				settings: new CommonSettingsView(frontend.settingsFrontend(itemSerializer)),
				chart: new TestChartView(frontend.chartFrontend(productSerializer))
			}),
			controllers: this.mergeControllers(controllers, {
				init: new InitTestController(),
				test: new TestController(),
				navigation: new PqNavigationController(),
				timer: new TimerController(),
				context: new ContextController(),
				filter: new FilterController(),
				settings: new SettingsController(),
				flag: new FlagController(),
			}, extra_controllers),
			content: new Content(),
			current: new Current(),
			filter: new Filter(),
			timer: new PqTimer(),
			userdata: libs?.userdata ?? new UserData(),
			navigation: new Navigation(),
			config
		});
	}

	static constructReview<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId>(
		datasource: PqDataSourceFactory<P, A, Z, I>,
		frontend: PqFrontendFactory<P, A, Z, I>,
		config: Config,
		serializer?: SerializerFactory<P, A, Z, I>,
		controllers?: Partial<{
			init: InitReviewController<P, A, Z, I>,
			navigation: PqNavigationController<P, A, Z, I>,
			timer: TimerController<P, A, Z, I, PqTimerExtras>,
			context: ContextController<P, A, Z, I>,
			filter: FilterController<P, A, Z, I>,
			settings: SettingsController<P, A, Z, I>,
			flag: FlagController<P, A, Z, I>,
		}>,
		extra_controllers?: Controller<P, A, Z, I>[],
		libs?: Partial<{
			userdata: UserData<P, A, Z, I>,
		}>,
	): PqApp<P, A, Z, I> {
		serializer ??= new DefaultSerializerFactory();
		const itemSerializer = serializer.itemSerializer();
		const quizSerializer = serializer.quizSerializer();
		const productSerializer = serializer.productSerializer();
		return new this({
			model: new Model({
				metadata: new ReviewMetadataModel(datasource.metadataDataSource()),
				list: new CommonListModel(datasource.listDataSource(itemSerializer)),
				item: new ReviewItemModel(datasource.itemDataSource()),
				score: new ReviewScoreModel(datasource.scoreDataSource(quizSerializer)),
				settings: new CommonSettingsModel(datasource.settingsDataSource()),
				state: {
					global: new CommonStateModel(datasource.globalStateDataSource(datasource.globalStateInitializer() ?? new GlobalStateInitializer())),
					attempt: new CommonStateModel(datasource.attemptStateDataSource(datasource.attemptStateInitializer() ?? new PqAttemptStateInitializer()))
				}
			}),
			view: new View({
				item: new ReviewItemView(frontend.itemFrontend(itemSerializer)),
				list: new CommonListView(frontend.listFrontend(itemSerializer)),
				header: new ReviewHeaderView(frontend.headerFrontend(itemSerializer)),
				scores: new ReviewScoreView(frontend.scoreFrontend()),
				settings: new CommonSettingsView(frontend.settingsFrontend(itemSerializer)),
				chart: new ReviewChartView(frontend.chartFrontend(productSerializer))
			}),
			controllers: this.mergeControllers(controllers, {
				init: new InitReviewController(),
				navigation: new PqNavigationController(),
				timer: new TimerController(),
				context: new ContextController(),
				filter: new FilterController(),
				settings: new SettingsController(),
				flag: new FlagController(),
			}, extra_controllers),
			content: new Content(),
			current: new Current(),
			filter: new Filter(),
			timer: new PqTimer(),
			userdata: libs?.userdata ?? new UserData(),
			navigation: new Navigation(),
			config
		});
	}
}

export class FlashApp<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends App<P, A, Z, I> {
	model: Model<P, A, Z, I, FlashTimerExtras>;
	view: FlashView<P, A, Z, I>;
	timer: FlashTimer;

	static constructCard<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId>(
		datasource: FlashDataSourceFactory<P, A, Z, I>,
		frontend: FlashFrontendFactory<P, A, Z, I>,
		config: Config,
		serializer?: SerializerFactory<P, A, Z, I>,
		controllers?: Partial<{
			init: InitCardController<P, A, Z, I>,
			card: CardController<P, A, Z, I>,
			navigation: FlashNavigationController<P, A, Z, I>,
			timer: TimerController<P, A, Z, I, FlashTimerExtras>,
			context: ContextController<P, A, Z, I>,
			filter: FilterController<P, A, Z, I>,
			settings: SettingsController<P, A, Z, I>,
			flag: FlagController<P, A, Z, I>,
		}>,
		extra_controllers?: Controller<P, A, Z, I>[],
	): FlashApp<P, A, Z, I> {
		serializer ??= new DefaultSerializerFactory();
		const itemSerializer = serializer.itemSerializer();
		const productSerializer = serializer.productSerializer();
		return new FlashApp({
			model: new Model({
				metadata: new CardMetadataModel(datasource.metadataDataSource()),
				list: new CommonListModel(datasource.listDataSource(itemSerializer)),
				item: new CardItemModel(datasource.itemDataSource()),
				settings: new CommonSettingsModel(datasource.settingsDataSource()),
				state: {
					global: new CommonStateModel(datasource.globalStateDataSource(datasource.globalStateInitializer() ?? new GlobalStateInitializer())),
					attempt: new CommonStateModel(datasource.attemptStateDataSource(datasource.attemptStateInitializer() ?? new FlashAttemptStateInitializer()))
				}
			}),
			view: new View({
				item: new CardItemView(frontend.itemFrontend(itemSerializer)),
				list: new CommonListView(frontend.listFrontend(itemSerializer)),
				header: new CardHeaderView(frontend.headerFrontend(itemSerializer)),
				settings: new CommonSettingsView(frontend.settingsFrontend(itemSerializer)),
				chart: new CardChartView(frontend.chartFrontend(productSerializer))
			}),
			controllers: this.mergeControllers(controllers, {
				init: new InitCardController(),
				card: new CardController(),
				navigation: new FlashNavigationController(),
				timer: new TimerController(),
				context: new ContextController(),
				filter: new FilterController(),
				settings: new SettingsController(),
				flag: new FlagController(),
			}, extra_controllers),
			content: new Content(),
			current: new Current(),
			filter: new Filter(),
			timer: new FlashTimer(),
			userdata: new UserData(),
			navigation: new Navigation(),
			config
		});
	}

	static constructList<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId>(
		datasource: FlashDataSourceFactory<P, A, Z, I>,
		frontend: FlashFrontendFactory<P, A, Z, I>,
		config: Config,
		serializer?: SerializerFactory<P, A, Z, I>,
		controllers?: Partial<{
			init: InitListController<P, A, Z, I>,
			filter: FilterController<P, A, Z, I>,
		}>,
		extra_controllers?: Controller<P, A, Z, I>[],
	): FlashApp<P, A, Z, I> {
		serializer ??= new DefaultSerializerFactory();
		const itemSerializer = serializer.itemSerializer();
		return new FlashApp({
			model: new Model({
				list: new CommonListModel(datasource.listDataSource(itemSerializer)),
				metadata: new CardMetadataModel(datasource.metadataDataSource()),
				state: {
					global: new CommonStateModel(datasource.globalStateDataSource(datasource.globalStateInitializer() ?? new GlobalStateInitializer())),
					attempt: new CommonStateModel(datasource.attemptStateDataSource(datasource.attemptStateInitializer() ?? new FlashAttemptStateInitializer()))
				}
			}),
			view: new View({
				list: new CommonListView(frontend.listFrontend(itemSerializer)),
				header: new ListHeaderView(frontend.headerFrontend(itemSerializer)),
			}),
			controllers: this.mergeControllers(controllers, {
				init: new InitListController(),
				filter: new FilterController(),
			}, extra_controllers),
			content: null,
			current: new Current(),
			filter: new Filter(),
			timer: null,
			userdata: new UserData(),
			navigation: new Navigation(),
			config
		});
	}
}
